import React, { useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Download } from "lucide-react";
import { trpcClient } from "@/lib/trpc";
import { formatMoney, cn } from "@/lib/utils";
import {
  ListPageShell,
  useServerListFilter,
} from "@/components/shared/ListPageShell";
import type { FilterField } from "@/components/shared/FilterBar";
import {
  DataTableRow,
  DataTableRowCheckbox,
  DataTableSelectAllCheckbox,
} from "@/components/shared/DataTableRow";
import { useRowSelection } from "@/hooks/useRowSelection";
import { BulkActionBar } from "@/components/shared/BulkActionBar";
import { toast } from "sonner";
import { ExportImportButtons } from "@/components/shared/ExportImportButtons";
import { useExcelExport } from "@/hooks/useExcelExport";
import { downloadExcelTemplate, type ExcelColumnDef } from "@/lib/excel.utils";

const EXPORT_COLUMNS: ExcelColumnDef[] = [
  { key: "code", label: "Mã Nhà Cung Cấp", width: 16 },
  { key: "name", label: "Tên Nhà Cung Cấp", width: 30 },
  { key: "shortName", label: "Tên Ngắn", width: 18 },
  { key: "initialDebt", label: "Dư Nợ Đầu Kỳ", width: 18, format: "currency" },
  { key: "currentDebt", label: "Dư Nợ Hiện Tại", width: 18, format: "currency" },
];

const FILTER_FIELDS: FilterField[] = [
  {
    key: "search",
    label: "Tìm kiếm",
    type: "text",
    placeholder: "Tên, mã NCC...",
  },
  {
    key: "hasDebt",
    label: "Trạng thái",
    type: "combobox-multi",
    options: [
      { value: "true", label: "Có nợ" },
      { value: "false", label: "Tất cả" },
    ],
  },
];

export default function APSummaryListPage() {
  const navigate = useNavigate();

  const {
    isLoading,
    filterValues,
    showFilter,
    currentPage,
    pageSize,
    totalItems,
    filtered,
    onPageChange,
    onPageSizeChange,
    onToggleFilter,
    onClearAll,
  } = useServerListFilter<any>({
    queryKey: "ap-summary",
    queryFn: (params: any) =>
      trpcClient.ap.getSummary.query({
        ...params,
        hasDebt: params.hasDebt?.includes("true") ? true : undefined,
      }) as any,
  });

  const rowIds = useMemo(() => filtered.map((c: any) => c.id), [filtered]);
  const rowSelection = useRowSelection(rowIds);
  const { selectedCount, selectedIds, clearSelection } = rowSelection;

  const { handleExport, isExporting } = useExcelExport({
    columns: EXPORT_COLUMNS,
    filename: "tong_hop_cong_no_phai_tra_ap",
    queryFn: (params: any) =>
      trpcClient.ap.getSummary.query({
        ...params,
        hasDebt: params.hasDebt?.includes("true") ? true : undefined,
      }) as any,
    currentFilters: filterValues,
    selectedIds,
  });

  return (
    <>
      <ListPageShell
        unitLabel="Nhà cung cấp"
        totalItems={totalItems}
        onToggleFilter={onToggleFilter}
        onClearAll={onClearAll}
        showFilter={showFilter}
        filterFields={FILTER_FIELDS}
        filterValues={filterValues}
        actions={
          <ExportImportButtons
            onExport={() => handleExport()}
            exportLoading={isExporting}
            onDownloadTemplate={() => downloadExcelTemplate(EXPORT_COLUMNS, "mau_tong_hop_cong_no_ap")}
          />
        }
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      >
        <div className="overflow-x-auto min-h-[50vh]">
          <table
            className={cn("w-full text-xs caption-bottom apv-compact-table")}
          >
            <thead>
              <tr className="border-b bg-slate-50/50">
                <DataTableSelectAllCheckbox rowSelection={rowSelection} />
                <th className="px-3 py-2 text-center font-medium text-slate-500 w-[60px]">
                  STT
                </th>
                <th className="px-3 py-2 text-left font-medium text-slate-500 w-[120px]">
                  Mã NCC
                </th>
                <th className="px-3 py-2 text-left font-medium text-slate-500 min-w-[200px]">
                  Tên Nhà Cung Cấp
                </th>
                <th className="px-3 py-2 text-right font-medium text-slate-500 w-[160px]">
                  Dư nợ đầu kỳ
                </th>
                <th className="px-3 py-2 text-right font-medium text-slate-500 w-[160px]">
                  Dư nợ hiện tại
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? (
                Array(5)
                  .fill(0)
                  .map((_, i) => (
                    <tr key={i}>
                      <td colSpan={6} className="p-3">
                        <div className="h-6 bg-slate-100 rounded animate-pulse" />
                      </td>
                    </tr>
                  ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-slate-400">
                    Không tìm thấy dữ liệu
                  </td>
                </tr>
              ) : (
                filtered.map((item: any, idx: number) => {
                  const initialDebt = Number(item.initialDebt || 0);
                  const currentDebt = Number(item.currentDebt || 0);

                  return (
                    <DataTableRow
                      key={item.id}
                      id={item.id}
                      onClick={() =>
                        navigate({
                          to: `/finance/ap/ledger/${item.id}` as any,
                        })
                      }
                      rowSelection={rowSelection}
                    >
                      <DataTableRowCheckbox id={item.id} rowSelection={rowSelection} />
                      <td className="px-2 py-2 text-center">
                        <span className="text-slate-400">
                          {(currentPage - 1) * pageSize + idx + 1}
                        </span>
                      </td>
                      <td className="apv-col-code px-3 py-2">
                        <span className="font-semibold text-slate-900 whitespace-nowrap leading-tight">
                          {item.code}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-slate-700 font-medium">
                        <span className="text-slate-800">{item.name}</span>
                        {item.shortName && (
                          <span className="ml-2 text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">
                            {item.shortName}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-medium text-slate-600">
                        {formatMoney(initialDebt)}
                      </td>
                      <td
                        className={`px-3 py-2 text-right font-bold ${currentDebt > 0 ? "text-rose-600" : currentDebt < 0 ? "text-emerald-600" : "text-slate-600"}`}
                      >
                        {formatMoney(currentDebt)}
                      </td>
                    </DataTableRow>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </ListPageShell>

      <BulkActionBar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        actions={[
          {
            icon: <Download className="h-4 w-4" />,
            label: `Xuất Excel ${selectedCount} dòng`,
            onClick: () => handleExport(),
          },
        ]}
      />
    </>
  );
}
